import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, Check, X, RefreshCw, Car, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { useTranslation } from '../../i18n';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VinConflicts = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);
  const [resolvingAll, setResolvingAll] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, conflict: null, action: null });

  const fetchConflicts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/vin-conflicts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConflicts(response.data.conflicts || []);
    } catch (error) {
      console.error('Error fetching conflicts:', error);
      toast.error('Failed to load VIN conflicts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConflicts();
  }, []);

  const handleResolveAll = async () => {
    try {
      setResolvingAll(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/vin-conflicts/resolve-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { added, failed } = response.data;
      toast.success(`${added} araç eklendi${failed > 0 ? `, ${failed} başarısız` : ''}`);
      fetchConflicts();
    } catch (error) {
      console.error('Error resolving all conflicts:', error);
      toast.error('Tümünü eklerken hata oluştu');
    } finally {
      setResolvingAll(false);
    }
  };

  // Heuristic: same year+brand+model => same car. Otherwise different.
  const isDifferentCar = (c) => {
    const ev = c.existing_vehicle || {};
    const nv = c.new_vehicle || {};
    const sameYear = (ev.year || 0) === (nv.year || 0) && (ev.year || 0) > 0;
    const sameBrand = (ev.brand || '').toLowerCase() === (nv.brand || '').toLowerCase();
    const sameModel = (ev.model || '').toLowerCase() === (nv.model || '').toLowerCase();
    const evMi = ev.mileage || 0;
    const nvMi = nv.mileage || 0;
    const mileageDiff = (evMi > 0 && nvMi > 0) ? Math.abs(evMi - nvMi) : 0;
    return !(sameYear && sameBrand && sameModel && mileageDiff <= 8000);
  };

  const handleAddAllDifferent = async () => {
    const targets = conflicts.filter(isDifferentCar);
    if (targets.length === 0) {
      toast.info(t('admin.noDifferentCars') || 'Eklenecek "farklı araç" bulunamadı');
      return;
    }
    try {
      setResolvingAll(true);
      const token = localStorage.getItem('token');
      let added = 0;
      let failed = 0;
      for (const c of targets) {
        try {
          await axios.post(`${API}/admin/vin-conflicts/${c.id}/resolve?action=add`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          added += 1;
        } catch {
          failed += 1;
        }
      }
      toast.success(`${added} farklı araç eklendi${failed > 0 ? `, ${failed} başarısız` : ''}`);
      fetchConflicts();
    } catch (error) {
      console.error('Error adding different cars:', error);
      toast.error(t('admin.operationFailed') || 'İşlem başarısız');
    } finally {
      setResolvingAll(false);
    }
  };

  const handleResolve = async (conflictId, action) => {
    try {
      setResolving(conflictId);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/vin-conflicts/${conflictId}/resolve?action=${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(action === 'add' ? 'Vehicle added successfully!' : 'Conflict skipped');
      fetchConflicts();
    } catch (error) {
      console.error('Error resolving conflict:', error);
      toast.error('An error occurred');
    } finally {
      setResolving(null);
      setConfirmDialog({ open: false, conflict: null, action: null });
    }
  };

  const openEbayListing = (itemNumber) => {
    window.open(`https://www.ebay.com/itm/${itemNumber}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (conflicts.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <Check className="w-16 h-16 mx-auto text-green-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('admin.vinConflictsTitle')}</h3>
        <p className="text-gray-600">{t('admin.allSynced')}</p>
        <Button onClick={fetchConflicts} className="mt-4" variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('admin.refresh')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
          <h2 className="text-xl font-semibold">{t('admin.vinConflictsTitle')}</h2>
          <Badge variant="destructive">{conflicts.length} {t('admin.pending')}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleAddAllDifferent}
            disabled={resolvingAll || conflicts.filter(isDifferentCar).length === 0}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
            data-testid="add-all-different-btn"
            title={t('admin.addAllDifferentTooltip') || 'Aynı VIN kullanan ama gerçekte farklı olan araçları ekle'}
          >
            {resolvingAll ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            {t('admin.addAllDifferent') || 'Farklı Araçları Ekle'} ({conflicts.filter(isDifferentCar).length})
          </Button>
          <Button 
            onClick={handleResolveAll} 
            disabled={resolvingAll}
            className="bg-green-600 hover:bg-green-700"
          >
            {resolvingAll ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            {t('admin.addAll') || 'Tümünü Ekle'} ({conflicts.length})
          </Button>
          <Button onClick={fetchConflicts} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('admin.refresh')}
          </Button>
        </div>
      </div>

      {/* Conflicts List */}
      <div className="space-y-4">
        {conflicts.map((conflict) => (
          <div key={conflict.id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {/* VIN Header */}
            <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Car className="w-5 h-5 text-gray-600" />
                <span className="font-mono font-medium text-gray-700">VIN: {conflict.vin}</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => openEbayListing(conflict.ebay_item_number)}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                eBay
              </Button>
            </div>

            {/* In-card explanation — dynamic based on whether existing & new look like the same vehicle */}
            {(() => {
              const ev = conflict.existing_vehicle || {};
              const nv = conflict.new_vehicle || {};
              const sameYear = (ev.year || 0) === (nv.year || 0) && (ev.year || 0) > 0;
              const sameBrand = (ev.brand || '').toLowerCase() === (nv.brand || '').toLowerCase();
              const sameModel = (ev.model || '').toLowerCase() === (nv.model || '').toLowerCase();
              const evMi = ev.mileage || 0;
              const nvMi = nv.mileage || 0;
              const mileageDiff = (evMi > 0 && nvMi > 0) ? Math.abs(evMi - nvMi) : 0;
              // Same car if year+brand+model match (mileage only used as a tiebreaker when both are present)
              const looksSame = sameYear && sameBrand && sameModel && mileageDiff <= 8000;
              const message = looksSame
                ? (t('admin.vinConflictSameCar') || 'Bu araç zaten ilanda var. Tekrar koymak ister misin?')
                : (t('admin.vinConflictDifferentCar') || "Bu araç aynı VIN'i kullanıyor ama yıl/mileage farklı — yani aynı araç değil.");
              return (
                <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5">
                  <p className="text-amber-900 text-sm">{message}</p>
                </div>
              );
            })()}

            {/* Comparison */}
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x">
              {/* Existing Vehicle */}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="secondary">{t('admin.existingVehicle')}</Badge>
                </div>
                {conflict.existing_vehicle?.images?.[0] && (
                  <img 
                    src={conflict.existing_vehicle.images[0]} 
                    alt="Existing" 
                    className="w-full h-32 object-cover rounded-lg mb-3"
                  />
                )}
                <h4 className="font-semibold text-gray-800">
                  {conflict.existing_vehicle?.year} {conflict.existing_vehicle?.brand} {conflict.existing_vehicle?.model}
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  eBay: {conflict.existing_vehicle?.ebay_item_number}
                </p>
                {conflict.existing_vehicle?.mileage > 0 && (
                  <p className="text-sm text-gray-500">
                    {t('admin.mileage')}: {conflict.existing_vehicle.mileage.toLocaleString()} mi
                  </p>
                )}
              </div>

              {/* New Vehicle */}
              <div className="p-4 bg-blue-50/50">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-blue-500">{t('admin.newListing')}</Badge>
                </div>
                {conflict.new_vehicle?.images?.[0] && (
                  <img 
                    src={conflict.new_vehicle.images[0]} 
                    alt="New" 
                    className="w-full h-32 object-cover rounded-lg mb-3"
                  />
                )}
                <h4 className="font-semibold text-gray-800">
                  {conflict.new_vehicle?.year} {conflict.new_vehicle?.brand} {conflict.new_vehicle?.model}
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  {t('admin.price')}: ${conflict.new_vehicle?.price?.toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">
                  {t('admin.mileage')}: {conflict.new_vehicle?.mileage?.toLocaleString()} mi
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-4 py-3 border-t flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setConfirmDialog({ open: true, conflict, action: 'skip' })}
                disabled={resolving === conflict.id}
              >
                <X className="w-4 h-4 mr-2" />
                {t('admin.skipConflict')}
              </Button>
              <Button
                onClick={() => setConfirmDialog({ open: true, conflict, action: 'add' })}
                disabled={resolving === conflict.id}
                className="bg-green-600 hover:bg-green-700"
              >
                {resolving === conflict.id ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {t('admin.addAsNewVehicle')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, conflict: null, action: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.action === 'add' ? 'Add Vehicle?' : 'Skip Conflict?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.action === 'add' ? (
                <>
                  <strong>{confirmDialog.conflict?.new_vehicle?.year} {confirmDialog.conflict?.new_vehicle?.brand} {confirmDialog.conflict?.new_vehicle?.model}</strong> 
                  {' '}will be added as a new vehicle. There will be two vehicles with the same VIN.
                </>
              ) : (
                <>
                  This conflict will be skipped and the new listing will not be added. The existing vehicle will be kept.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleResolve(confirmDialog.conflict?.id, confirmDialog.action)}
              className={confirmDialog.action === 'add' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {confirmDialog.action === 'add' ? 'Yes, Add' : 'Yes, Skip'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VinConflicts;
